import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/site/SEO";
import Reveal from "../components/site/Reveal";
import PricingTable from "../components/site/PricingTable";
import { useDevis } from "../components/site/DevisForm";
import { useConfig } from "../components/Layout";
import { IMAGES, TARIFS_DEFAUT } from "../lib/constants";

const SECTIONS = [
  {
    titre: "Montage de pneus",
    image: IMAGES.montage,
    alt: "Montage d'un pneu sur jante à la machine à démonter",
    texte: [
      "Un montage soigné et rapide, qui tient compte des paramètres techniques de votre véhicule : dimension, indice de charge, indice de vitesse, pression préconisée. Rien n'est laissé au hasard.",
      "Remplacement de pneus usés, rotation saisonnière, montage de pneus achetés ailleurs : tout est possible, sans rendez-vous. Vous arrivez, on s'occupe du reste.",
    ],
  },
  {
    titre: "Équilibrage des roues",
    image: IMAGES.equilibrage,
    alt: "Équilibrage d'une roue : rotation sur la machine, affichage des masses",
    texte: [
      "Une roue mal équilibrée use vos pneus prématurément, augmente votre consommation de carburant et provoque des vibrations dans la direction dès 90 km/h.",
      "Nos machines d'équilibrage de précision corrigent le problème en quelques minutes. Chaque pneu monté à l'atelier est équilibré systématiquement.",
    ],
  },
  {
    titre: "Réparation de crevaison",
    image: IMAGES.crevaison,
    alt: "Réparation d'une crevaison : mèche insérée dans la bande de roulement",
    texte: [
      "Chaque crevaison est d'abord inspectée pour déterminer si une réparation est sûre : emplacement du trou, état du flanc, usure générale. On ne répare pas ce qui ne doit pas l'être.",
      "Quand la réparation est possible, nous utilisons des techniques éprouvées. Quand elle ne l'est pas, nous vous proposons un remplacement en neuf ou en occasion contrôlée.",
    ],
  },
  {
    titre: "Types de pneus pris en charge",
    image: IMAGES.pneusStock,
    alt: "Stock de pneus neufs et occasions à l'atelier",
    texte: [
      "Voiture, 4x4, utilitaire, camionnette, voiture sans permis : l'atelier équipe presque tout ce qui roule. Pneus neufs et occasions contrôlées, été, hiver et toutes saisons.",
      "Votre dimension n'est pas en stock ? Nous la commandons sous 24 à 48 h auprès de nos fournisseurs.",
    ],
  },
];

export default function Pneumatique() {
  const config = useConfig();
  const tarif = config?.tarifs?.pneus || TARIFS_DEFAUT.pneus;
  const { openDevis } = useDevis();

  return (
    <>
      <SEO
        title="Pneumatiques au Cannet — Montage, équilibrage, crevaison | Promo Pneus"
        description="Montage de pneus dès 20 € / pneu au Cannet, près de Cannes. Équilibrage dès 10 €, réparation de crevaison dès 15 €. Pneus neufs et occasions, sans rendez-vous."
      />
      <section className="bg-brand-dark py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-heading font-semibold uppercase tracking-[0.3em] text-brand-red text-base mb-3">
              {"//"} Chapitre 01
            </p>
            <h1 className="font-heading font-bold uppercase text-5xl sm:text-6xl md:text-7xl text-white tracking-wide leading-none">
              Pneumatiques
            </h1>
            <p className="mt-4 text-lg text-white/80 max-w-2xl">
              Montage, équilibrage, crevaison. Neufs et occasions, toutes marques, toutes dimensions.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <PricingTable titre={tarif.titre} rows={tarif.rows} testid="tarifs-pneus" />
        </div>
      </section>

      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.titre}>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center`}>
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <p className="font-heading font-bold text-6xl text-brand-light leading-none mb-2" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="font-heading font-semibold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-5">
                    {s.titre}
                  </h2>
                  {s.texte.map((p, j) => (
                    <p key={j} className="text-brand-body leading-relaxed text-base md:text-lg mb-4">{p}</p>
                  ))}
                </div>
                <div className={`relative ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-brand-red" aria-hidden="true" />
                  <img src={s.image} alt={s.alt} className="w-full h-72 sm:h-96 object-cover relative" loading="lazy" />
                </div>
              </div>
              {i === 0 && (
                <p className="mt-8 bg-brand-light border-l-4 border-brand-red px-5 py-4 text-brand-dark font-body leading-relaxed" data-testid="pneus-rappel-parallelisme">
                  Bon réflexe : le montage de pneus neufs est le moment idéal pour vérifier le
                  parallélisme — les roues sont déjà démontées et le contrôle est rapide. Un mauvais
                  réglage userait vos pneus neufs prématurément.{" "}
                  <Link to="/parallelisme" data-testid="pneus-lien-parallelisme"
                    className="text-brand-red font-semibold underline hover:text-brand-redhover transition-colors">
                    Découvrir le réglage du parallélisme
                  </Link>
                </p>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 bg-brand-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold uppercase text-3xl sm:text-4xl text-brand-dark tracking-tight mb-6">
              Pourquoi choisir Promo Pneus pour vos pneus
            </h2>
            <p className="text-brand-body text-lg leading-relaxed mb-4">
              Plus de 20 ans à monter des pneus au Cannet. Nous stockons du neuf et de l'occasion contrôlée,
              et chaque pneu d'occasion est inspecté avant d'être proposé : profondeur de gomme, flancs, hernies.
            </p>
            <p className="text-brand-body text-lg leading-relaxed mb-8">
              Les prix sont affichés, l'équilibrage est fait à chaque montage, et vous repartez avec une
              pression contrôlée. Sans rendez-vous : vous passez quand vous voulez.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button data-testid="pneus-devis-bouton" onClick={() => openDevis("Demande concernant : pneumatiques. ")}
                className="bg-brand-red hover:bg-brand-redhover text-white font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
                Demander un devis pneus
              </button>
              {config?.catalogue_visible !== false && (
                <Link to="/catalogue" data-testid="pneus-catalogue-lien"
                  className="inline-flex items-center justify-center gap-2 bg-brand-dark hover:bg-black text-white font-heading font-semibold uppercase tracking-wider text-lg px-8 py-4 transition-colors">
                  Voir le catalogue <ArrowRight size={18} aria-hidden="true" />
                </Link>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
